/* ---------------------------------------------------------------------------
 * The glass hopper: a small rigid-body sim on a canvas, ported numerically
 * unchanged from dir-lottery.js:750-950.
 *
 * Every constant below is tuned rather than derived — a "tidier" number changes
 * the churn — so they are copied verbatim. What did change is ownership: the
 * class has no timer of its own, no React knowledge, and no DOM outside the
 * canvas it was handed. The meter, the pips and the "Empty" plate were mutated
 * from inside `flyBall` in the mockup; they are React now, driven off
 * `remaining`.
 * ------------------------------------------------------------------------ */

export interface Pt {
  x: number
  y: number
}

export interface HopperBall {
  pick: number
  label: string
}

interface Body {
  pick: number
  label: string
  x: number
  y: number
  vx: number
  vy: number
  a: number
  ph: number
  /** Sim seconds before this ball is released above the glass. 0 = already in. */
  delay: number
  /** False until it has fallen far enough to join the churn. */
  entered: boolean
}

const clamp = (v: number, a: number, b: number) => (v < a ? a : v > b ? b : v)

/* How long the pour takes to release every ball, in ms. Sized against
 * lotteryTimeline's spin-up window so the tank is full and churning before the
 * first ball is drawn out of it. */
const POUR_MS = 900

/* A ball is round, so the usable text box is a square inside it rather than the
 * full diameter. These are fractions of the radius. */
const TEXT_W = 1.5
const TEXT_H = 1.45
export const LINE_H = 1.14
const MAX_LINES = 3

/** Average glyph width as a fraction of the font size, for 700-weight Plex Sans
 *  in caps. Used wherever the text cannot be cheaply measured — the SVG flyer
 *  and the rail's socket, which size themselves in CSS. The canvas measures for
 *  real. */
export const CHAR_W = 0.62

/**
 * A franchise name broken into at most three balanced lines, so it can be
 * written across a ball instead of reduced to two initials.
 *
 * Balanced rather than greedy: greedy wrapping puts "Two Minute" on one line
 * and "Warning" alone on the next, which sets the font size off the long line
 * and wastes the short one. Exported so the canvas sprite and the SVG flyer
 * break the same name the same way.
 */
export function ballLines(name: string, lines: number): string[] {
  const words = String(name ?? '')
    .trim()
    .split(/\s+/)
    .filter(Boolean)
  if (!words.length) return [ '--' ]
  const k = Math.min(lines, words.length)
  if (k <= 1) return [ words.join(' ') ]

  // Exhaustive rather than greedy. Greedy wrapping puts "Two Minute" on one
  // line and "Warning" alone on the next, which sets the font size off the long
  // line and wastes the short one; the search is over at most a handful of
  // split points for a team name, so just take the best one.
  let best: string[] = [ words.join(' ') ]
  let bestMax = Infinity
  const split = (from: number, left: number, acc: string[]) => {
    if (left === 1) {
      const candidate = [ ...acc, words.slice(from).join(' ') ]
      const longest = Math.max(...candidate.map((line) => line.length))
      if (longest < bestMax) {
        bestMax = longest
        best = candidate
      }
      return
    }
    for (let to = from + 1; to <= words.length - (left - 1); to++) {
      split(to, left - 1, [ ...acc, words.slice(from, to).join(' ') ])
    }
  }
  split(0, k, [])
  return best
}

export interface BallText {
  lines: string[]
  /** Longest line, in characters — what a CSS or SVG caller sizes against. */
  chars: number
}

/**
 * The line break to use for a name on a ball, chosen without measuring: it
 * compares candidates by the font size each would allow, using CHAR_W, and the
 * ball's radius cancels out of that comparison. Shared so the SVG flyer and the
 * rail's socket break a name the same way the canvas sprite does.
 */
export function fitBallText(name: string): BallText {
  const upper = String(name ?? '')
    .trim()
    .toUpperCase()
  if (!upper) return { lines: [ '--' ], chars: 2 }

  let best: BallText = { lines: [ upper ], chars: upper.length }
  let bestSize = 0
  for (let n = 1; n <= MAX_LINES; n++) {
    const lines = ballLines(upper, n)
    if (lines.length < n && n > 1) break
    const chars = Math.max(...lines.map((line) => line.length))
    const size = Math.min(TEXT_W / (chars * CHAR_W), TEXT_H / (lines.length * LINE_H))
    if (size > bestSize) {
      bestSize = size
      best = { lines, chars }
    }
  }
  return best
}

/** Two characters, from dir-lottery.js:553-558. No longer used by the lottery —
 *  every ball carries its whole name — but kept as the fallback shape. */
export function initials(name: string): string {
  const parts = String(name ?? '')
    .replace(/[^A-Za-z0-9]+/g, ' ')
    .split(' ')
    .filter(Boolean)
  if (!parts.length) return '--'
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase()
  return (parts[0].charAt(0) + parts[parts.length - 1].charAt(0)).toUpperCase()
}

export class HopperSim {
  private readonly canvas: HTMLCanvasElement
  private readonly stage: HTMLElement
  private ctx: CanvasRenderingContext2D | null

  private bodies: Body[] = []
  private sprites = new Map<string, HTMLCanvasElement | null>()
  private simW = 0
  private simH = 0
  private simR = 0
  private simCorner = 0
  private simT = 0
  private boostValue = 1

  /** `stage` is only ever used to convert a removed ball's position into stage
   *  coordinates for the flight layer. The canvas's own rect is the glass box —
   *  .lot-cv is `position:absolute; inset:0` inside .lot-glass — so there is no
   *  third element to pass. */
  constructor(canvas: HTMLCanvasElement, stage: HTMLElement) {
    this.canvas = canvas
    this.stage = stage
    // jsdom has no canvas backend and package.json carries no `canvas`
    // dependency, so this throws there rather than returning null.
    let ctx: CanvasRenderingContext2D | null = null
    try {
      ctx = canvas.getContext('2d')
    } catch {
      ctx = null
    }
    this.ctx = ctx
  }

  /** Sizes the canvas, builds the sprites and seeds one body per ball.
   *  False when there is no 2d context or the canvas has no box yet.
   *
   *  `pour` drops them in from above the glass, staggered, instead of starting
   *  with a full tank — the moment the countdown reaches zero is the one
   *  everybody is looking at, and a hopper that simply *is* full wastes it.
   *  The physics needs nothing new: yf clamps to 0 above the box, so the
   *  updraft cannot touch a ball until it is inside, and it falls on gravity
   *  alone. */
  init(balls: HopperBall[], { pour = false }: { pour?: boolean } = {}): boolean {
    if (!this.sizeToBox()) return false
    this.sprites.clear()
    this.simT = 0
    this.bodies = balls.map((b, i) => ({
      pick: b.pick,
      label: b.label,
      x: pour
        ? this.simW * (0.22 + 0.56 * Math.random())
        : this.simW * (0.18 + (0.64 * ((i % 4) + 0.5)) / 4),
      // Above the lip, so the first thing the viewer sees is the ball arriving.
      y: pour ? -this.simR * 1.5 : this.simH * (0.3 + (0.5 * (Math.floor(i / 4) + 0.5)) / 3),
      vx: pour ? (Math.random() - 0.5) * this.simH * 0.25 : (Math.random() - 0.5) * this.simH * 1.6,
      vy: pour ? this.simH * 0.35 : (Math.random() - 0.5) * this.simH * 1.2,
      a: Math.random() * 6.28,
      ph: Math.random() * 6.28,
      // Spread across POUR_MS so they arrive one after another rather than as a
      // single clump. Reversed, so the last ball in is the one drawn first and
      // the pour reads as a countdown. Nothing depends on the order: the
      // agitator runs ~2s more before the first draw and scrambles the stack,
      // and the draw itself comes off the wire, not off the physics.
      delay: pour ? (POUR_MS / 1000) * ((balls.length - 1 - i) / Math.max(1, balls.length - 1)) : 0,
      entered: !pour,
    }))
    this.buildSprites()
    return true
  }

  /** Re-sizes after a layout change, rescaling live bodies so nothing pops. */
  resize(): boolean {
    const oldW = this.simW
    const oldH = this.simH
    if (!this.sizeToBox()) return false
    if (oldW > 0 && oldH > 0) {
      const kx = this.simW / oldW
      const ky = this.simH / oldH
      for (const b of this.bodies) {
        b.x *= kx
        b.y *= ky
        b.vx *= kx
        b.vy *= ky
      }
    }
    this.sprites.clear()
    this.buildSprites()
    return true
  }

  step(dtSeconds: number): void {
    // Clamped here rather than in the caller's loop. A tab restored from the
    // background delivers one enormous frame delta, and an unclamped 4-second
    // dt puts every ball through the wall in a single integration step. The
    // mockup clamped in simLoop; a sim that explodes on a large dt is a
    // footgun for every future caller, so it owns the clamp.
    const dt = Math.min(0.034, Math.max(0, dtSeconds))
    if (!this.ctx || !this.bodies.length || !this.simH) return
    this.simT += dt

    const g = this.simH * 2.6 * this.boostValue
    const jetB = this.simH * 7.9 * this.boostValue
    const damp = Math.pow(0.9915, dt * 60)
    const maxV = this.simH * 4.4

    for (const b of this.bodies) {
      if (this.simT < b.delay) continue // still queued above the lip
      if (!b.entered) {
        // Falling in. Gravity only: no jet, no swirl, no walls — the rounded
        // rect would otherwise snap it straight into the tank and there would
        // be no pour to watch.
        b.vy += this.simH * 2.6 * this.boostValue * dt
        b.x += b.vx * dt
        b.y += b.vy * dt
        b.a += b.vx * dt * 0.02
        if (b.y >= this.simCorner) b.entered = true
        continue
      }
      const yf = clamp(b.y / this.simH, 0, 1)
      const jet = jetB * Math.pow(yf, 1.75)
      const swirl =
        Math.sin(this.simT * 2.35 + b.ph) * this.simH * 1.7 +
        Math.sin(this.simT * 0.83 + b.ph * 1.9) * this.simH * 0.95
      const toward = (this.simW * 0.5 - b.x) * 4.4 * Math.pow(yf, 1.25)
      b.vx += (swirl + toward) * dt
      b.vy += (g - jet) * dt
      if (Math.random() < dt * 3.2) {
        b.vx += (Math.random() - 0.5) * this.simH * 1.5 * this.boostValue
        b.vy += (Math.random() - 0.5) * this.simH * 1.2 * this.boostValue
      }
      b.vx *= damp
      b.vy *= damp
      const sp = Math.hypot(b.vx, b.vy)
      if (sp > maxV) {
        b.vx *= maxV / sp
        b.vy *= maxV / sp
      }
      b.x += b.vx * dt
      b.y += b.vy * dt
      b.a += (b.vx / (this.simR * 6)) * dt * 4
    }

    for (let i = 0; i < this.bodies.length; i++) {
      for (let j = i + 1; j < this.bodies.length; j++) {
        const a = this.bodies[i]
        const c = this.bodies[j]
        if (!a.entered || !c.entered) continue
        const dx = c.x - a.x
        const dy = c.y - a.y
        const dd = Math.hypot(dx, dy)
        const min = this.simR * 2
        if (dd < min && dd > 0.0001) {
          const nx = dx / dd
          const ny = dy / dd
          const overlap = (min - dd) * 0.5
          a.x -= nx * overlap
          a.y -= ny * overlap
          c.x += nx * overlap
          c.y += ny * overlap
          const rel = (c.vx - a.vx) * nx + (c.vy - a.vy) * ny
          if (rel < 0) {
            const imp = rel * 0.92
            a.vx += imp * nx
            a.vy += imp * ny
            c.vx -= imp * nx
            c.vy -= imp * ny
            a.a -= imp * 0.002
            c.a += imp * 0.002
          }
        }
      }
    }

    for (const b of this.bodies) if (b.entered) this.bound(b)
  }

  draw(): void {
    const ctx = this.ctx
    if (!ctx || !this.simW) return
    ctx.clearRect(0, 0, this.simW, this.simH)
    const d = this.simR * 2
    for (const b of this.bodies) {
      if (this.simT < b.delay) continue
      const sprite = this.sprites.get(b.label)
      // A sprite can be null even when the main context is not.
      if (!sprite) continue
      // A motion-blur ghost first, then the ball.
      ctx.save()
      ctx.globalAlpha = 0.22
      ctx.translate(b.x - b.vx * 0.01, b.y - b.vy * 0.01)
      ctx.rotate(b.a)
      ctx.drawImage(sprite, -this.simR, -this.simR, d, d)
      ctx.restore()
      ctx.save()
      ctx.globalAlpha = 1
      ctx.translate(b.x, b.y)
      ctx.rotate(b.a)
      ctx.drawImage(sprite, -this.simR, -this.simR, d, d)
      ctx.restore()
    }
  }

  /** The settled grid used before spin-up and by the reduced-motion path. */
  drawStatic(): void {
    const ctx = this.ctx
    if (!ctx || !this.simW) return
    ctx.clearRect(0, 0, this.simW, this.simH)
    const d = this.simR * 2
    const cols = 4
    this.bodies.forEach((b, i) => {
      b.delay = 0
      b.entered = true
      const row = Math.floor(i / cols)
      const col = i % cols
      b.x = this.simW * 0.5 + (col - (cols - 1) / 2) * (d * 1.06)
      b.y = this.simH - this.simR * 1.2 - row * (d * 0.94)
      b.a = 0
      const sprite = this.sprites.get(b.label)
      if (!sprite) return
      ctx.save()
      ctx.translate(b.x, b.y)
      ctx.drawImage(sprite, -this.simR, -this.simR, d, d)
      ctx.restore()
    })
  }

  /** Removes the body and returns its centre in *stage* coordinates. */
  remove(pick: number): Pt | null {
    const i = this.bodies.findIndex((b) => b.pick === pick)
    if (i < 0) return null
    const at = this.toStage(this.bodies[i])
    this.bodies.splice(i, 1)
    return at
  }

  positionOf(pick: number): Pt | null {
    const b = this.bodies.find((body) => body.pick === pick)
    return b ? this.toStage(b) : null
  }

  get remaining(): number {
    return this.bodies.length
  }

  set boost(value: number) {
    this.boostValue = value
  }

  destroy(): void {
    this.bodies = []
    this.sprites.clear()
    this.ctx = null
  }

  /* ---------------------------------------------------------------------- */

  private sizeToBox(): boolean {
    if (!this.ctx) return false
    const b = this.canvas.getBoundingClientRect()
    // jsdom's getBoundingClientRect() is all zeros, and so is the real one
    // before first paint.
    if (!b.width || !b.height) return false
    this.simW = b.width
    this.simH = b.height
    const dpr = Math.min(2, window.devicePixelRatio || 1)
    this.canvas.width = Math.round(this.simW * dpr)
    this.canvas.height = Math.round(this.simH * dpr)
    this.ctx.setTransform(dpr, 0, 0, dpr, 0, 0)
    this.simR = this.simW * 0.0705
    this.simCorner = Math.max(this.simR + 2, this.simW * 0.148)
    return true
  }

  private buildSprites(): void {
    for (const b of this.bodies) {
      // Keyed on the label, so two teams with the same initials share one
      // bitmap.
      if (!this.sprites.has(b.label)) this.sprites.set(b.label, this.makeSprite(b.label, this.simR))
    }
  }

  /** The largest font size at which `name` fits the ball, and the lines it
   *  breaks into to get there. Measured rather than estimated — the canvas can
   *  measure, and guessing a per-character width is what makes long names
   *  overflow on one device and float in whitespace on another. */
  private fitName(
    x: CanvasRenderingContext2D,
    name: string,
    r: number,
  ): { lines: string[]; size: number } {
    let best = { lines: [ name ], size: 0 }
    for (let n = 1; n <= MAX_LINES; n++) {
      const lines = ballLines(name, n)
      if (lines.length < n && n > 1) break // no more words to split on
      x.font = '700 100px "IBM Plex Sans", system-ui, sans-serif'
      const widest = Math.max(...lines.map((l) => x.measureText(l).width))
      const byWidth = ((r * TEXT_W) / widest) * 100
      const byHeight = (r * TEXT_H) / (lines.length * LINE_H)
      const size = Math.min(byWidth, byHeight)
      if (size > best.size) best = { lines, size }
    }
    return best
  }

  private makeSprite(text: string, r: number): HTMLCanvasElement | null {
    const ss = 2 // 2x supersample
    const s = Math.max(8, Math.ceil(r * 2 * ss))
    const c = document.createElement('canvas')
    c.width = s
    c.height = s
    let x: CanvasRenderingContext2D | null = null
    try {
      x = c.getContext('2d')
    } catch {
      x = null
    }
    if (!x) return null
    const R = s / 2
    const g = x.createRadialGradient(R * 0.68, R * 0.6, R * 0.06, R, R, R)
    g.addColorStop(0, '#ffffff')
    g.addColorStop(0.5, '#f3f0e6')
    g.addColorStop(1, '#bcb5a4')
    x.beginPath()
    x.arc(R, R, R - ss, 0, Math.PI * 2)
    x.fillStyle = g
    x.fill()
    x.lineWidth = ss * 1.1
    x.strokeStyle = 'rgba(0,0,0,.34)'
    x.stroke()
    x.beginPath()
    x.arc(R * 0.66, R * 0.58, R * 0.22, 0, Math.PI * 2)
    x.fillStyle = 'rgba(255,255,255,.85)'
    x.fill()
    x.fillStyle = '#171b24'
    x.textAlign = 'center'
    x.textBaseline = 'middle'
    // The whole franchise name, wrapped and scaled to fit, rather than two
    // initials: on a ball this size the name is the thing the room is reading.
    const { lines, size } = this.fitName(x, text.toUpperCase(), R)
    x.font = `700 ${size.toFixed(1)}px "IBM Plex Sans", system-ui, sans-serif`
    const step = size * LINE_H
    const top = R + R * 0.02 - ((lines.length - 1) * step) / 2
    lines.forEach((line, i) => x.fillText(line, R, top + i * step))
    return c
  }

  private bound(b: Body): void {
    const rIn = this.simCorner
    const qx = clamp(b.x, rIn, this.simW - rIn)
    const qy = clamp(b.y, rIn, this.simH - rIn)
    const dx = b.x - qx
    const dy = b.y - qy
    const d = Math.hypot(dx, dy)
    const lim = rIn - this.simR
    if (d > lim && d > 0.0001) {
      const nx = dx / d
      const ny = dy / d
      b.x = qx + nx * lim
      b.y = qy + ny * lim
      const vn = b.vx * nx + b.vy * ny
      if (vn > 0) {
        b.vx -= 1.58 * vn * nx
        b.vy -= 1.58 * vn * ny
        b.vx *= 0.94
        b.vy *= 0.94
        b.a += (b.vx * ny - b.vy * nx) * 0.0016
      }
    }
  }

  private toStage(b: Body): Pt {
    const stage = this.stage.getBoundingClientRect()
    const cv = this.canvas.getBoundingClientRect()
    return { x: cv.left - stage.left + b.x, y: cv.top - stage.top + b.y }
  }
}
